/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef } from "react";

export type EditorLike = {
	selection?: {
		getBookmark?: (type?: number, normalized?: boolean) => unknown;
		moveToBookmark?: (bookmark: unknown) => void;
		getNode?: () => Element | null;
		getRng?: () => Range | null;
		select?: (node: Element, content?: boolean) => void;
		collapse?: (toStart?: boolean) => void;
	};
	focus?: () => void;
	hasFocus?: () => boolean;
	getBody?: () => Element | null;
	insertContent?: (content: string) => void;
	fire?: (event: string) => void;
	id?: string;
	on?: (event: string, handler: () => void) => void;
	off?: (event: string, handler: () => void) => void;
};

const getTinyMce = (): any => (typeof window !== "undefined" ? (window as any).tinymce : null);

export const getEditorBookmark = (editor: EditorLike): unknown | undefined => {
	try {
		return editor.selection?.getBookmark?.(2, true);
	} catch {
		return undefined;
	}
};

const PLACEHOLDER_SELECTOR = "span[data-placeholder]";

/** How often to re-check for the lazily loaded `window.tinymce` global. */
const TINYMCE_POLL_INTERVAL_MS = 200;

/**
 * Placeholder pills are `contenteditable="false"` spans, so clicking one makes
 * TinyMCE select the whole element. Inserting over that selection would
 * *replace* the existing pill. When the restored selection sits on (or spans)
 * a pill, collapse the caret to just after it so the insert adds content
 * instead of overwriting the pill. Plain text selections keep the standard
 * replace-on-insert behavior.
 */
export const collapseSelectionAfterPlaceholder = (editor: EditorLike): void => {
	try {
		const selection = editor.selection;
		const node = selection?.getNode?.();

		const pill =
			node && typeof node.closest === "function" ? node.closest(PLACEHOLDER_SELECTOR) : null;

		if (pill) {
			selection?.select?.(pill);
			selection?.collapse?.(false);

			return;
		}

		const range = selection?.getRng?.();

		if (range && !range.collapsed) {
			const fragment = range.cloneContents?.();

			if (fragment?.querySelector?.(PLACEHOLDER_SELECTOR)) {
				selection?.collapse?.(false);
			}
		}
	} catch {
		// Best effort — fall back to the default insert behavior.
	}
};

/**
 * Tracks the most recently focused TinyMCE editor on the page and lets callers
 * insert content at the user's last cursor position, even after focus has
 * moved out of the editor (e.g. into a modal).
 *
 * This works entirely off TinyMCE's global registry, so parent components do
 * not need to forward editor refs.
 */
export const useEditorCursorInsertion = () => {
	const lastFocusedEditorRef = useRef<EditorLike | null>(null);
	const lastBookmarkRef = useRef<unknown>(null);

	useEffect(() => {
		const wireEditor = (editor: EditorLike) => {
			const onFocus = () => {
				// A bookmark is a caret *inside one editor*; replaying the body's
				// bookmark in the subject editor lands the token at a random offset.
				if (lastFocusedEditorRef.current !== editor) {
					lastBookmarkRef.current = null;
				}

				lastFocusedEditorRef.current = editor;
			};

			const onBlur = () => {
				const bookmark = getEditorBookmark(editor);

				if (bookmark) {
					lastBookmarkRef.current = bookmark;
					lastFocusedEditorRef.current = editor;
				}
			};

			editor.on?.("focus", onFocus);
			editor.on?.("blur", onBlur);
		};

		let tinymce: any = null;
		let onAddEditor: ((e: { editor: EditorLike }) => void) | null = null;

		const wireRegistry = (): boolean => {
			tinymce = getTinyMce();

			if (!tinymce) {
				return false;
			}

			// Wire editors that already exist, then the ones added later. TinyMCE 6+
			// dropped the `editors` array; `get()` without an id lists them.
			const existing: EditorLike[] = Array.isArray(tinymce.editors)
				? tinymce.editors
				: (tinymce.get?.() ?? []);

			existing.forEach(wireEditor);

			onAddEditor = (e) => wireEditor(e.editor);
			tinymce.on?.("AddEditor", onAddEditor);

			// Seed lastFocused only with an editor the user is actually in: TinyMCE
			// also marks the first editor it creates as "active", and that one is
			// not a choice the user made.
			if (tinymce.activeEditor?.hasFocus?.()) {
				lastFocusedEditorRef.current = tinymce.activeEditor;
			}

			return true;
		};

		// TinyMCE is fetched lazily by the first <Editor> that mounts — usually in
		// the same render as this hook's host — so the global is normally still
		// missing here. Poll until it shows up instead of giving up once: the
		// campaign editor's Variables panel did exactly that and never learned
		// which editor was focused, so every subject insert fell back to the
		// message body (PREC-4015).
		let poll: number | undefined;

		if (!wireRegistry()) {
			poll = window.setInterval(() => {
				if (wireRegistry()) {
					window.clearInterval(poll);
					poll = undefined;
				}
			}, TINYMCE_POLL_INTERVAL_MS);
		}

		return () => {
			if (poll !== undefined) {
				window.clearInterval(poll);
			}

			if (tinymce && onAddEditor) {
				tinymce.off?.("AddEditor", onAddEditor);
			}
		};
	}, []);

	const captureCurrentPosition = useCallback(() => {
		const tinymce = getTinyMce();
		const active: EditorLike | null = tinymce?.activeEditor || lastFocusedEditorRef.current;

		// Only snapshot while the editor still holds focus. Capturing from a
		// blurred editor would store a stale/empty caret and clobber a good
		// bookmark already taken on blur (e.g. after the user clicked the panel
		// search box, which moves focus out of the editor but leaves the correct
		// position saved).
		if (!active || !active.hasFocus?.()) {
			return;
		}

		const bookmark = getEditorBookmark(active);

		if (bookmark) {
			lastBookmarkRef.current = bookmark;
			lastFocusedEditorRef.current = active;
		}
	}, []);

	const insertAtCursor = useCallback((token: string) => {
		const editor = lastFocusedEditorRef.current;

		if (!editor) {
			return false;
		}

		try {
			editor.focus?.();

			if (lastBookmarkRef.current) {
				editor.selection?.moveToBookmark?.(lastBookmarkRef.current);
			}

			collapseSelectionAfterPlaceholder(editor);

			editor.insertContent?.(token);
			editor.fire?.("change");

			// Refresh the bookmark to the position after insertion so subsequent
			// inserts continue in the right place.
			const updated = getEditorBookmark(editor);

			if (updated) {
				lastBookmarkRef.current = updated;
			}

			return true;
		} catch {
			return false;
		}
	}, []);

	const hasFocusedEditor = useCallback((): boolean => {
		return Boolean(lastFocusedEditorRef.current);
	}, []);

	return { captureCurrentPosition, insertAtCursor, hasFocusedEditor };
};

export type UseEditorCursorInsertionReturn = ReturnType<typeof useEditorCursorInsertion>;
